import { createApi } from '@reduxjs/toolkit/query/react';

import { ChatMessageType, ChatMessageServerData } from '@typings/show';
import { User } from '@typings/user';
import { chatSlice } from '@redux/state/chatSlice';
import { usersSlice } from '@redux/state/usersSlice';
import { CHAT_MESSAGES_PER_PAGE } from '@utils/constants';

import { dynamicBaseQuery } from '../baseQuery';

export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Chat'],
  endpoints: (builder) => ({
    sendChatMessage: builder.mutation<
      ChatMessageServerData,
      {
        showId: string;
        content: string;
        type: ChatMessageType;
        optimisticMessageId?: string;
      }
    >({
      // eslint-disable-next-line
      query: ({ optimisticMessageId, ...body }) => {
        return {
          url: '/chat-message',
          method: 'POST',
          body,
        };
      },
      onQueryStarted: async (
        { showId, optimisticMessageId },
        { dispatch, queryFulfilled },
      ) => {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            const { show, user, ...message } = data;
            dispatch(
              chatSlice.actions.setChatMessageById({
                ...message,
                showId: show.id,
                userId: user.id,
              }),
            );
            if (optimisticMessageId) {
              dispatch(
                chatSlice.actions.replaceOptimisticChatMessageByShowId({
                  showId: show.id,
                  optimisticId: optimisticMessageId,
                  realMessageId: data.id,
                  isSuccess: true,
                }),
              );
            } else {
              dispatch(
                chatSlice.actions.addMessageToShowIds({
                  showId: show.id,
                  id: data.id,
                }),
              );
            }
            if (user.settings?.usernameColor) {
              dispatch(
                usersSlice.actions.setUserDataById({
                  id: user.id,
                  usernameColor: user.settings?.usernameColor,
                }),
              );
            }
          }
        } catch (error) {
          if (optimisticMessageId) {
            dispatch(
              chatSlice.actions.replaceOptimisticChatMessageByShowId({
                showId,
                optimisticId: optimisticMessageId,
                isSuccess: false,
              }),
            );
          }

          console.log('sendChatMessage error', error);
        }
      },
    }),
    changePinnedStatusForChatMessage: builder.mutation<
      boolean,
      {
        id: string;
        showId: string;
      }
    >({
      query: ({ id }) => {
        return {
          url: `/chat-message/${id}`,
          method: 'PUT',
        };
      },
      onQueryStarted: async ({ id, showId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            chatSlice.actions.setChatMessageById({
              id,
              pinned: data,
            }),
          );
          dispatch(
            chatSlice.actions.updatePinnedChatMessageByShowId({
              id: data ? id : null,
              showId,
            }),
          );
        } catch (error) {
          console.log('changePinnedStatusForChatMessage error', error);
        }
      },
    }),
    getChatMessagesByShowId: builder.query<
      ChatMessageServerData[],
      {
        showId: string;
        lastId?: string;
        isRefresh: boolean;
        perPage?: number;
      }
    >({
      query: ({ showId, lastId, perPage = CHAT_MESSAGES_PER_PAGE }) => {
        return {
          url: `shows/${showId}/chat-messages`,
          params: { lastId, perPage },
          method: 'GET',
        };
      },
      keepUnusedDataFor: 1,
      onQueryStarted: async (
        { showId, isRefresh },
        { dispatch, queryFulfilled },
      ) => {
        try {
          const { data } = await queryFulfilled;
          if (data.length) {
            const ids = data.map(({ id }) => id);
            const usersData: Record<string, User> = {};
            const formattedMessages = data.map(({ show, user, ...message }) => {
              usersData[user.id] = {
                id: user.id,
                name: user?.name,
                usernameColor: user.settings?.usernameColor,
              };
              return {
                ...message,
                userId: user.id,
                showId: show.id,
              };
            });
            dispatch(chatSlice.actions.setChatMessages(formattedMessages));
            dispatch(
              chatSlice.actions.setChatMessagesByShowId({
                showId,
                ids,
                isRefresh,
              }),
            );
            dispatch(usersSlice.actions.setUsersData(Object.values(usersData)));
          } else {
            dispatch(
              chatSlice.actions.setChatMessagesByShowId({
                showId,
                ids: [],
                isRefresh,
              }),
            );
          }
        } catch (error) {
          console.log('getChatMessagesByShowId error', error);
        }
      },
    }),
    getPinnedChatMessagesByShowId: builder.query<
      ChatMessageServerData,
      { showId: string }
    >({
      query: ({ showId }) => {
        return {
          url: `shows/${showId}/chat-messages/pinned`,
          method: 'GET',
        };
      },
      keepUnusedDataFor: 1,
      onQueryStarted: async ({ showId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            const { show, user, ...message } = data;
            const userData = {
              id: user.id,
              name: user?.name,
              usernameColor: user.settings?.usernameColor,
            };
            const pinnedMessage = {
              ...message,
              userId: user.id,
              showId: show.id,
            };

            dispatch(chatSlice.actions.setChatMessageById(pinnedMessage));
            dispatch(usersSlice.actions.setUserDataById(userData));
            dispatch(
              chatSlice.actions.updatePinnedChatMessageByShowId({
                showId,
                id: pinnedMessage.id,
              }),
            );
          } else {
            dispatch(
              chatSlice.actions.updatePinnedChatMessageByShowId({
                showId,
                id: null,
              }),
            );
          }
        } catch (error) {
          console.log('getPinnedChatMessagesByShowId error', error);
        }
      },
    }),
  }),
});

export const {
  useSendChatMessageMutation,
  useChangePinnedStatusForChatMessageMutation,
  useLazyGetChatMessagesByShowIdQuery,
  useLazyGetPinnedChatMessagesByShowIdQuery,
} = chatApi;
